import { datadogRum } from '@datadog/browser-rum';
import apiClient from '@shared/lib/general/apiClient';
import {
  ADMIN_CHANGE_VALUES,
  ADMIN_LOGOUT,
} from '@shared/store/constants/mutationTypes';
import launchDarklyClient from '../../lib/general/launchDarklyClient';

const actions = {
  changeAdminValues({ commit }, changes) {
    commit({
      type: ADMIN_CHANGE_VALUES,
      changes,
    });
  },
  async getUserInfo() {
    try {
      const { data: userInfo } = await apiClient({
        method: 'GET',
        url: '/admins/info',
      });
      return userInfo;
    } catch (err) {
      console.error(err);
    }
  },
  async getUserProvider(_, email) {
    const { data: provider } = await apiClient({
      method: 'GET',
      url: `/auth/provider/admin/${email}`,
    });
    return provider;
  },
  async getDomainPool(_, email) {
    const { data: pool } = await apiClient({
      method: 'GET',
      url: `/domain/pool/${encodeURIComponent(email)}`,
    });
    return pool || null;
  },
  async getWatchlistData({ dispatch }) {
    const { data: watchlist } = await apiClient({
      method: 'GET',
      url: '/watchlist/list',
    });
    dispatch('changeAdminValues', [{
      field: 'watchlistData',
      value: watchlist,
    }]);
  },
  async createWatchlist({ dispatch, state }, payload) {
    const { data } = await apiClient({
      method: 'POST',
      url: '/watchlist/new',
      data: {
        ...payload,
        priority: state.watchlistData[state.watchlistData.length - 1]
          ? state.watchlistData[state.watchlistData.length - 1].priority + 1 : 1,
      },
    });
    dispatch('changeAdminValues', [{
      field: 'watchlistData',
      value: [...state.watchlistData, data],
    }]);
  },
  async updateWatchlist({ dispatch, state }, payload) {
    await apiClient({
      method: 'PUT',
      url: '/watchlist/update',
      data: payload,
    });
    const old = state.watchlistData.filter((x) => x.id !== payload.id);
    if (payload.isActive) {
      old.push(payload);
    }
    dispatch('changeAdminValues', [{
      field: 'watchlistData',
      value: old,
    }]);
  },
  async updateWatchlists({ dispatch }, payload) {
    const { data } = await apiClient({
      method: 'PUT',
      url: '/watchlist/update/all',
      data: payload,
    });
    dispatch('changeAdminValues', [{
      field: 'watchlistData',
      value: data,
    }]);
  },
  async searchCustomerList(_, payload) {
    try {
      const { data: customerList } = await apiClient({
        method: 'POST',
        url: '/search/customer/query',
        data: payload,
      });
      return customerList;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  async searchTechnicianList(_, payload) {
    try {
      const { data: customerList } = await apiClient({
        method: 'POST',
        url: '/search/tech/query',
        data: payload,
      });
      return customerList;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  async getCurrentAdminInfo({ dispatch, state }) {
    dispatch('changeAdminValues', [{
      field: 'loading',
      value: true,
    }]);

    const currentUser = JSON.parse(localStorage.currentUser);
    const { data: current } = await apiClient({
      method: 'GET',
      url: `/admins/id/${currentUser.customerId}`,
    });

    const { data } = await apiClient({
      method: 'GET',
      url: '/partner/settings',
    });
    const {
      corporation,
      corporationId,
    } = current;

    const { data: plans } = await apiClient({
      method: 'GET',
      url: `/provider/plans/corp/${corporationId}/all`,
    });
    const {
      id: userId,
      companyName,
    } = current;
    datadogRum.setUser({
      id: `${userId}`,
      name: companyName,
      userId,
      companyName,
    });

    await launchDarklyClient.initialize(process.env.LAUNCH_DARKLY_CLIENT_SIDE_ID, {
      kind: 'user',
      key: `${userId}`,
      company: companyName,
      corpId: `${corporationId}`,
    });

    let branches = [];
    if (corporationId === 1) {
      const { data: pBranches } = await apiClient({
        method: 'GET',
        url: '/corporations/partners/all',
      });

      const addParentList = [];

      pBranches.forEach((c) => {
        addParentList.push(c);
        if (c.parent) {
          addParentList.push({
            id: c.id * -1,
            corporationName: `${c.corporationName} - All`,
            parent: true,
          });
        }
      });
      branches = addParentList.sort((a, b) => a.corporationName.localeCompare(b.corporationName));
    } else if (corporation.parent) {
      const { data: pBranches } = await apiClient({
        method: 'GET',
        url: `/corporations/id/${corporation.id}/branches`,
      });
      const sorted = pBranches.sort((a, b) => a.corporationName.localeCompare(b.corporationName));
      branches = [{
        id: current.corporation.id * -1,
        corporationName: `${corporation.corporationName} - All`,
      },
      ...sorted,
      ];
    }
    const partnerSettings = {};
    Object.keys(data).forEach((key) => {
      let value = data[key];
      if (value === 'true') {
        value = true;
      } else if (value === 'false') {
        value = false;
      }
      if (value[0] === '[' || value[0] === '{') {
        value = JSON.parse(data[key]);
      }
      partnerSettings[key] = value;
    });

    // eslint-disable-next-line
    if (this._vm.$intercom) {
      // eslint-disable-next-line
      this._vm.$intercom.shutdown();
      if (!localStorage[`${current.email}-super-login-check`]) {
        // eslint-disable-next-line
        this._vm.$intercom.boot({
          user_id: `${current.id}`,
          email: current.email,
          name: `${current.firstName} ${current.lastName} - ${current.companyName} - NOC `,
        });
      } else {
        // eslint-disable-next-line
        this._vm.$intercom.boot();
      }
    }

    const {
      id,
    } = current.corporation;

    dataLayer.push({ partner_corp_id: `${id}` });
    // if (chrome?.runtime) {
    //   const payload = {
    //     action: 'set-token',
    //     nocToken: currentUser,
    //   };
    //   chrome.runtime.sendMessage('aljmnkngemdbgpnknhlknlfgblohmmmb', payload);
    // }

    // if (chrome?.runtime) {
    //   const payload = {
    //     action: 'set-token',
    //     nocToken: currentUser,
    //   };
    //   chrome.runtime.sendMessage('mdghjjnjdaceedbjdfibdogodhghmkde', payload);
    // }

    const existingAdminFilter = localStorage.getItem('adminFilter')
      ? JSON.parse(localStorage.getItem('adminFilter')) : {
        ...state.adminFilter,
        corpId: corporation.id,
        corpName: corporation.corporationName,
      };

    dispatch('changeAdminValues', [{
      field: 'current',
      value: current,
    }, {
      field: 'corporation',
      value: current.corporation,
    }, {
      field: 'partnerSettings',
      value: partnerSettings,
    }, {
      field: 'partnerList',
      value: branches,
    }, {
      field: 'loading',
      value: false,
    }, {
      field: 'adminFilter',
      value: existingAdminFilter,
    }, {
      field: 'appOnly',
      value: plans.filter((x) => x.planType === 'Hardware').length === 0 && plans.filter((x) => x.planType === 'AppOnly').length >= 0,
    }, {
      field: 'systemNotification',
      value: current.notifications?.length > 0 ? current.notifications[0] : null,
    }]);
  },
  logoutAdmin({ commit }) {
    commit({
      type: ADMIN_LOGOUT,
    });
  },
};

export default actions;
